// CONNECTIONS-AREA (docs/design/2026-10-30-connections-area.md §B–§E; founder decisions
// 2026-10-30): Team › Connections (`/c/{slug}/agents/connections`, `?tab=`, `?whose=mine`), one
// place for everything people and DEs log in to. Five tabs, each with its count (none when empty):
// - Sign-in apps: OAuth connections (Google, Slack, GitHub…) as rows, and the catalog to connect
//   another (Connect opens a modal: the vendor's sign-in);
// - API keys: key connections to one host (Add key: a modal) and, as decided, the AI model keys
//   (an "AI models" group; Model policy keeps the rules);
// - Accounts: the saved logins, the same list as Live › Accounts (Log in opens it in Live);
// - Agent keys (managers): the keys that let agents into CompanyOS (agent-keys.tsx);
// - Access: the agents × connections matrix (who may use what).
// A Company | Mine toggle (managers; members see Mine): company rows are shared and granted to DEs
// by admins; Mine are the caller's own (My subscriptions renders the same components). One grant
// model: a new connection is closed to every DE until it is granted (migration 0134 granted the
// ones that existed to every DE at Read). "Paths" (a modal) holds the company's subscription-path
// switches. Setup › Keys & connections opens this page (subscriptions-routes.tsx); webhooks and
// imports stay in Setup › Integrations (a pointer at the foot). Never sample rows.
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InfoTip,
  PageFrame,
  PageHeader,
  SearchField,
  Segmented,
  ViewTabs,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PlusIcon, SlidersHorizontalIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { AccountsPanel } from "../agent-screens/accounts-panel";
import { type BrowserAccount, browserAccountsQuery } from "../agent-screens/screens-queries";
import { type CompanyAccess, isManager } from "../company";
import { LoadError, Loading } from "../screen-parts";
import { CliCard } from "../subscriptions/cli-card";
import { PathSwitches } from "../subscriptions/company-setup";
import { ClosedNotice } from "../subscriptions/my-subscriptions";
import { ConnectionForm, CredentialList, KeyForm } from "../subscriptions/parts";
import {
  type Connection,
  connectionsQuery,
  credentialsQuery,
  type Policy,
  policyQuery,
} from "../subscriptions/queries";
import { useFeature, useSession } from "../use-feature";
import { AccessMatrixScreen } from "./access-matrix";
import { AgentKeysTab, agentKeysQuery } from "./agent-keys";
import { Catalog } from "./catalog";
import { ConnectionRows } from "./connection-rows";
import { useAllPages } from "./grants";
import { ModelAccess } from "./model-access";

export const CONNECTION_TABS = ["apps", "keys", "accounts", "agent-keys", "access"] as const;
export type ConnectionTab = (typeof CONNECTION_TABS)[number];
export type ConnectionScope = "company" | "mine";

const TAB_LABEL: Record<ConnectionTab, string> = {
  apps: "Sign-in apps",
  keys: "API keys",
  accounts: "Accounts",
  "agent-keys": "Agent keys",
  access: "Access",
};

const live = (c: Connection) => c.state !== "disconnected";

export function ConnectionsArea({
  api,
  company,
  tab: asked = "apps",
  scope: askedScope = "company",
  onTab,
  onScope,
  onLogIn,
}: {
  api: Api;
  company: CompanyAccess;
  tab?: ConnectionTab;
  scope?: ConnectionScope;
  onTab: (t: ConnectionTab) => void;
  onScope: (s: ConnectionScope) => void;
  /** Accounts › Log in: the login browser opens in Live, which holds the viewer. */
  onLogIn: (account: BrowserAccount) => void;
}) {
  const co = company.id;
  const manager = isManager(company);
  // Members see their own (Mine); the company's rows and the agent keys are for managers.
  const scope: ConnectionScope = manager ? askedScope : "mine";
  const tabs = CONNECTION_TABS.filter(
    (t) => t !== "agent-keys" || (manager && scope === "company"),
  );
  const tab: ConnectionTab = tabs.includes(asked) ? asked : "apps";
  const agents = useFeature("agents", co);
  const { me } = useSession();
  const myMembership = me?.memberships.find((m) => m.company_id === co)?.membership_id ?? null;
  const policy = useQuery(policyQuery(api, co));
  const conns = useAllPages(useInfiniteQuery(connectionsQuery(api, co)));
  const creds = useAllPages(useInfiniteQuery(credentialsQuery(api, co)));
  const accounts = useQuery({ ...browserAccountsQuery(api, co), enabled: agents.enabled });
  const keys = useQuery({ ...agentKeysQuery(api, co), enabled: manager && scope === "company" });
  const [paths, setPaths] = useState(false);
  const want = scope === "company" ? "company" : "person";
  const connRows = (conns.data?.pages.flatMap((p) => p.data) ?? []).filter((c) => c.scope === want);
  const credRows = (creds.data?.pages.flatMap((p) => p.data) ?? []).filter((c) => c.scope === want);
  const count: Partial<Record<ConnectionTab, number>> = {
    apps: connRows.filter((c) => c.provider !== "api_key" && live(c)).length,
    keys:
      connRows.filter((c) => c.provider === "api_key" && live(c)).length +
      credRows.filter((c) => c.state === "active").length,
    accounts: (accounts.data?.data ?? []).filter(
      (a) => scope === "company" || a.owner.membership_id === myMembership,
    ).length,
    "agent-keys": (keys.data?.data ?? []).filter((k) => k.status === "active").length,
  };
  return (
    <PageFrame width="doc" data-slot="connections-area">
      <PageHeader
        spacing="tabs"
        eyebrow="Team"
        title="Connections"
        badge={
          <InfoTip label="About connections">
            Everything your people and DEs log in to. A new connection is closed to every DE until
            someone grants it.
          </InfoTip>
        }
        actions={
          <span className="flex items-center gap-2">
            {manager ? (
              <>
                <Segmented
                  size="sm"
                  aria-label="Whose connections"
                  value={scope}
                  onValueChange={onScope}
                  options={[
                    { value: "company", label: "Company" },
                    { value: "mine", label: "Mine" },
                  ]}
                />
                <InfoTip label="Company or mine">
                  Company connections are shared and granted to DEs by admins. Mine are yours; you
                  choose which of your DEs may use them.
                </InfoTip>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Paths"
                  title="Paths"
                  onClick={() => setPaths(true)}
                >
                  <SlidersHorizontalIcon aria-hidden="true" />
                </Button>
              </>
            ) : null}
          </span>
        }
      />
      <ViewTabs
        aria-label="Connections"
        value={tab}
        onValueChange={(v) => onTab(v as ConnectionTab)}
        tabs={tabs.map((t) => ({
          value: t,
          label: TAB_LABEL[t],
          ...(count[t] ? { count: count[t] } : {}),
        }))}
      />
      <div
        role="tabpanel"
        aria-label={TAB_LABEL[tab]}
        className="flex min-w-0 flex-col gap-4 pt-5"
        data-tab={tab}
        data-scope={scope}
      >
        {tab === "access" ? (
          <AccessMatrixScreen api={api} co={co} />
        ) : tab === "agent-keys" ? (
          <AgentKeysTab api={api} company={company} />
        ) : tab === "accounts" ? (
          agents.enabled ? (
            <>
              <AccountsPanel
                api={api}
                co={co}
                admin={manager}
                loggingIn={false}
                onLogIn={onLogIn}
                {...(scope === "mine" ? { ownedBy: myMembership } : {})}
              />
              {/* The screens and the viewer live in Live: open it on its Accounts tab. */}
              <Link
                to="/c/$slug/live"
                params={{ slug: company.slug }}
                search={{ tab: "accounts" }}
                className="self-start text-xs text-muted-foreground underline underline-offset-2"
              >
                Open screens in Live
              </Link>
            </>
          ) : (
            <p className="text-sm text-muted-foreground" data-slot="accounts-off">
              Saved logins come with the Agents add-on.
            </p>
          )
        ) : policy.isPending || conns.isPending || creds.isPending ? (
          <Loading label="Loading connections" rows={3} />
        ) : policy.isError ? (
          <LoadError what="Connections" error={policy.error} />
        ) : conns.isError ? (
          <LoadError what="Connections" error={conns.error} />
        ) : tab === "apps" ? (
          <SignInApps
            key={want}
            api={api}
            company={company}
            policy={policy.data}
            scope={want}
            rows={connRows.filter((c) => c.provider !== "api_key")}
          />
        ) : (
          // Keyed by scope: a half-typed key never changes whose it is (Codex r1)
          <ApiKeys
            key={want}
            api={api}
            company={company}
            policy={policy.data}
            scope={want}
            rows={connRows.filter((c) => c.provider === "api_key")}
            creds={credRows}
            credsError={creds.isError ? creds.error : null}
          />
        )}
        <p className="border-t border-subtle pt-3 text-xs text-muted-foreground">
          Webhooks and imports are in{" "}
          <Link
            to="/c/$slug/setup/webhooks"
            params={{ slug: company.slug }}
            search={keepFocus}
            className="underline underline-offset-2"
          >
            Setup › Integrations
          </Link>
          .
        </p>
      </div>
      <Dialog open={paths} onOpenChange={setPaths}>
        <DialogContent className="sm:max-w-lg" data-slot="paths-dialog">
          <DialogHeader>
            <DialogTitle>Paths</DialogTitle>
            <DialogDescription>Which ways people may bring keys and tools here.</DialogDescription>
          </DialogHeader>
          {policy.data ? (
            <PathSwitches api={api} company={company} policy={policy.data} />
          ) : (
            <Loading label="Loading paths" rows={2} />
          )}
        </DialogContent>
      </Dialog>
    </PageFrame>
  );
}

/** A search over label, provider and identity (the old Checks tab's search). */
function useSearch(rows: Connection[]) {
  const [term, setTerm] = useState("");
  const t = term.trim().toLowerCase();
  const shown = t
    ? rows.filter((c) =>
        [c.label, c.provider, c.identity_display ?? "", c.config?.host ?? ""].some((s) =>
          s.toLowerCase().includes(t),
        ),
      )
    : rows;
  const field =
    rows.length > 5 ? (
      <SearchField
        value={term}
        placeholder="Search connections…"
        aria-label="Search connections"
        onChange={(e) => setTerm(e.target.value)}
      />
    ) : null;
  return { shown, field };
}

function Heading({ title, info }: { title: string; info: string }) {
  return (
    <h2 className="flex items-center gap-1 text-sm font-medium">
      {title}
      <InfoTip label={`About ${title.toLowerCase()}`}>{info}</InfoTip>
    </h2>
  );
}

function SignInApps({
  api,
  company,
  policy,
  scope,
  rows,
}: {
  api: Api;
  company: CompanyAccess;
  policy: Policy;
  scope: "company" | "person";
  rows: Connection[];
}) {
  const open = policy.effective.tool_connections;
  const { shown, field } = useSearch(rows);
  return (
    <>
      <section className="flex flex-col gap-2" aria-label="Sign-in apps">
        <div className="flex items-center gap-2">
          <Heading
            title={scope === "company" ? "Company sign-ins" : "My sign-ins"}
            info="Accounts linked by signing in at the vendor. Status is the last check; a call ledger for last use comes with the connectors."
          />
          <span className="ml-auto">{field}</span>
        </div>
        <ConnectionRows
          api={api}
          co={company.id}
          slug={company.slug}
          rows={shown}
          label={scope === "company" ? "Company connections" : "My connections"}
          empty="No sign-in apps yet."
        />
      </section>
      {open ? null : (
        <ClosedNotice
          what="Tool connections"
          platformOn
          slug={company.slug}
          canManage={policy.can_manage}
        />
      )}
      <section className="flex flex-col gap-2" aria-label="Connect an app">
        <h2 className="text-sm font-medium">Connect an app</h2>
        <Catalog
          api={api}
          co={company.id}
          slug={company.slug}
          scope={scope}
          connections={rows}
          disabled={!open}
          kind="oauth"
        />
      </section>
    </>
  );
}

function ApiKeys({
  api,
  company,
  policy,
  scope,
  rows,
  creds,
  credsError,
}: {
  api: Api;
  company: CompanyAccess;
  policy: Policy;
  scope: "company" | "person";
  rows: Connection[];
  creds: Parameters<typeof CredentialList>[0]["rows"];
  credsError: unknown;
}) {
  const [adding, setAdding] = useState(false);
  const toolsOpen = policy.effective.tool_connections;
  const keysOpen =
    scope === "company" ? policy.effective.company_api_keys : policy.effective.person_api_keys;
  const { shown, field } = useSearch(rows);
  return (
    <>
      <section className="flex flex-col gap-2" aria-label="Tool keys">
        <div className="flex items-center gap-2">
          <Heading
            title="Tool keys"
            info="A key for one tool's host, for tools without a sign-in (a GoHighLevel private token). Shown once; only its last four after."
          />
          <span className="ml-auto flex items-center gap-2">
            {field}
            <Button size="sm" disabled={!toolsOpen} onClick={() => setAdding(true)}>
              <PlusIcon aria-hidden="true" />
              Add key
            </Button>
          </span>
        </div>
        <ConnectionRows
          api={api}
          co={company.id}
          slug={company.slug}
          rows={shown}
          label={scope === "company" ? "Company tool keys" : "My tool keys"}
          empty="No tool keys yet."
        />
        {toolsOpen ? null : (
          <ClosedNotice
            what="Tool connections"
            platformOn
            slug={company.slug}
            canManage={policy.can_manage}
          />
        )}
      </section>
      <section className="flex flex-col gap-2" aria-label="AI models" id="ai-models">
        {/* MODEL-KEYS-PLANS: ours (billed) or the company's own keys, when the plan allows it. */}
        {scope === "company" && policy.can_manage ? (
          <ModelAccess api={api} co={company.id} />
        ) : null}
        {credsError ? (
          <LoadError what="AI model keys" error={credsError} />
        ) : (
          <CredentialList
            api={api}
            co={company.id}
            rows={creds}
            title={scope === "company" ? "AI models" : "My AI models"}
            empty="No AI model keys"
          >
            {keysOpen ? (
              <KeyForm api={api} co={company.id} scope={scope} disabled={false} />
            ) : (
              <ClosedNotice
                what={scope === "company" ? "Company API keys" : "Personal API keys"}
                platformOn={
                  scope === "company"
                    ? policy.platform.company_api_keys
                    : policy.platform.person_api_keys
                }
                slug={company.slug}
                canManage={policy.can_manage}
              />
            )}
          </CredentialList>
        )}
        {scope === "person" ? (
          <CliCard
            api={api}
            co={company.id}
            open={policy.effective.person_cli_subscriptions}
            canManage={policy.can_manage}
          />
        ) : null}
      </section>
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="sm:max-w-xl" data-slot="add-key-dialog">
          <DialogHeader>
            <DialogTitle>Add a tool key</DialogTitle>
            <DialogDescription>
              {scope === "company" ? "A company connection" : "Your own connection"}
            </DialogDescription>
          </DialogHeader>
          <ConnectionForm
            api={api}
            co={company.id}
            scope={scope}
            disabled={!toolsOpen}
            onDone={() => setAdding(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
